import {
  FeedbackNotFoundException,
  GetFeedbackCommand,
  GetFeedbackCommandInput,
  GetFeedbackCommandOutput,
} from '@amzn/gbsa-helios-typescript-client';
import {
  QueryKey,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { apiHeliosClient } from 'src/data/api/apiHeliosClient';
import { feedbackKeys } from 'src/data/api/helios-service-ts/feedback/queries/keys';

/**
 * React Query hook for fetching feedback data from the Helios service.
 *
 * This hook automatically handles caching, refetching, and error states.
 * If the feedback is not found (FeedbackNotFoundException), it returns null
 * instead of throwing an error. Other errors are logged and re-thrown.
 *
 * @template TData - The type of the transformed data returned by the query.
 *                   Defaults to `GetFeedbackCommandOutput`.
 *
 * @param {GetFeedbackCommandInput} input - The input parameters for the GetFeedbackCommand,
 *                                          used to identify which feedback to fetch.
 *
 * @param {Omit<UseQueryOptions<GetFeedbackCommandOutput | null, Error, TData, QueryKey>, 'queryKey' | 'queryFn'>} [options] - Optional React Query configuration options.
 *                                                                                                                              The `queryKey` and `queryFn` are automatically set
 *                                                                                                                              and cannot be overridden.
 *
 * @returns {ReturnType<typeof useQuery<GetFeedbackCommandOutput | null, Error, TData, QueryKey>>} A React Query result object containing:
 *                                                                                                  - `data`: The feedback data or null if not found
 *                                                                                                  - `isLoading`: Loading state
 *                                                                                                  - `error`: Error object if the query failed
 *                                                                                                  - Other standard React Query properties
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useFeedbackQuery({ feedbackId: '123' });
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <Error message={error.message} />;
 * if (!data) return <NotFound />;
 *
 * return <FeedbackDisplay feedback={data} />;
 * ```
 */
export const useFeedbackQuery = <TData = GetFeedbackCommandOutput>(
  input: GetFeedbackCommandInput,
  options?: Omit<
    UseQueryOptions<GetFeedbackCommandOutput | null, Error, TData, QueryKey>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    ...options,
    queryKey: feedbackKeys.get(input),
    queryFn: async () => {
      try {
        return await apiHeliosClient.send(new GetFeedbackCommand(input));
      } catch (error) {
        if (error instanceof FeedbackNotFoundException) {
          return null;
        }
        console.error(error);
        throw error;
      }
    },
  });
};
